<template>
  <div class="store">
    <ui-title v-if="category">{{ category }}</ui-title>
    <div class="container">
      <div class="store__wrapper">
        <vue-marquee-slider
            id="marquee-slider"
            :speed="20000"
            :space="15"
            :paused="isMarqueePaused"
            @mouseenter="isMarqueePaused = true"
            @mouseleave="isMarqueePaused = false"
            v-if="brands"
        >
          <router-link :to="translations.i18nRoute({ name: 'store', query: { brand: brand.node.name }})"
              v-for="brand in brands"
              :key="brand.node.uuid"
              class="store__brand"
          >
            {{ brand.node.name }}
          </router-link>
        </vue-marquee-slider>
        <div class="store__top">
          <p>{{ brand ? brand : '' }}</p>
          <div class="store__sorting">
            <ui-button class="store__sorting-button" @click="setOrdering('price')">
              <svg width="14" height="7" viewBox="0 0 14 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.77766 0.278323L14 5.65585L12.4447 7L7 2.29454L1.55531 7L-5.87545e-08 5.65585L6.22234 0.278323C6.42861 0.100113 6.70834 8.4405e-07 7 8.18552e-07C7.29166 7.93054e-07 7.57139 0.100113 7.77766 0.278323Z" fill="white"/>
              </svg>
              {{ t('buttons.lowToHigh') }}
            </ui-button>
            <ui-button class="store__sorting-button" @click="setOrdering('-price')">
              <svg width="14" height="7" viewBox="0 0 14 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.22234 6.72168L-5.87545e-08 1.34415L1.55531 0L7 4.70546L12.4447 0L14 1.34415L7.77766 6.72168C7.57139 6.89989 7.29166 7 7 7C6.70834 7 6.42861 6.89989 6.22234 6.72168Z" fill="white"/>
              </svg>
              {{ t('buttons.highToLow') }}
            </ui-button>
          </div>
        </div>
        <div class="store__grid" v-if="products.length">
          <product-card
            v-for="product in products"
            :key="product.node.uuid"
            :product="product.node"
          />
          <div class="store__grid-observer" ref="observer"></div>
        </div>
        <div class="store__grid" v-if="isLoading">
          <product-card-skeleton v-for="idx in 20" :key="idx" />
        </div>
        <p v-if="!products.length && !isLoading" class="store__empty">{{ t('store.empty') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import {Brands} from "@/core/api/brands.js";
import {computed, onMounted, ref, watch} from "vue";
import { VueMarqueeSlider } from 'vue3-marquee-slider';
import '../../node_modules/vue3-marquee-slider/dist/style.css'
import {useIntersectionObserver} from "@vueuse/core";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {Products} from "@/core/api/products.js";
import {debounce} from "@/core/helpers/debounce.js";
import ProductCard from "@/components/cards/product-card.vue";
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import UiButton from "@/components/ui/ui-button.vue";
import translations from "@/core/helpers/translations.js";
import UiTitle from "@/components/ui/ui-title.vue";

const {t} = useI18n()
const route = useRoute()

const isMarqueePaused = ref(false)
const brands = ref([])

const isLoading = ref(true)
const observer = ref(null)

const { stop } = useIntersectionObserver(
  observer,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting && pageInfo.value.hasNextPage) {
      getMoreProducts()
    }
  },
)

const setOrdering = (method) => {
  orderBy.value = method
}

const products = ref([])
const pageInfo = ref([])
const minPrice = ref(route.query['minPrice'] ? (route.query['minPrice']) : '')
const maxPrice = ref(route.query['maxPrice'] ? (route.query['maxPrice']) : '')
const cursorForAfter = ref('0')
const name = ref(route.query['name'] ? (route.query['name']) : '')
const orderBy = ref(route.query['orderBy'] ? (route.query['orderBy']) : '')
const brand = computed(() => route.query['brand'])
const category = computed(() => route.query['category'])

const query = computed(() => {
  let res =`?orderBy=${orderBy.value}&minPrice=${minPrice.value}&maxPrice=${maxPrice.value}`

  if (name.value) {
    res = res + `&name=${name.value}`
  }

  if (brand.value) {
    res = res + `&brand=${brand.value}`
  }

  if (category.value) {
    res = res + `&categoriesSlugs=${category.value}`
  }

  return res
})

const getProducts = async () => {
  isLoading.value = true
  cursorForAfter.value = '0'

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 20` +
      (category?.value?.length ? `, categoriesSlugs: "${category.value}"` : "") +
      `, orderBy: "${orderBy.value}"` +
      (minPrice.value ? `, minPrice: "${minPrice.value}"` : "") +
      (maxPrice.value ? `, maxPrice: "${maxPrice.value}"` : "") +
      (brand.value ? `, brand: "${brand.value}"` : "") +
      (name.value ? `, name: "${name.value}"` : "")
  )
  products.value = res.data?.products?.edges
  pageInfo.value = res.data?.products?.pageInfo

  isLoading.value = false
}

const getMoreProducts = async () => {
  isLoading.value = true

  cursorForAfter.value = pageInfo.value.endCursor

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 20` +
      (category?.value?.length ? `, categoriesSlugs: "${category.value}"` : "") +
      `, orderBy: "${orderBy.value}"` +
      (minPrice.value ? `, minPrice: "${minPrice.value}"` : "") +
      (maxPrice.value ? `, maxPrice: "${maxPrice.value}"` : "") +
      (brand.value ? `, brand: "${brand.value}"` : "") +
      (name.value ? `, name: "${name.value}"` : "")
  )
  products.value.push(...res.data.products.edges)
  pageInfo.value = res.data.products.pageInfo

  isLoading.value = false
}

watch(
  () => query.value,
  async () => {

    products.value = [];
    history.pushState({}, '', route.path + query.value);
    await getProducts();
  }
)

const updateNameDebounced = debounce(() => {
  name.value = route.query['name'];
}, 500);
watch(
  () => route.query['name'],
  () => {
    updateNameDebounced();
  }
)

const getBrands = async () => {
  const res = await Brands.getBrands()
  brands.value = res.data.brands.edges
}

onMounted( async () => {
  await getBrands()
  await getProducts()
})
</script>

<style lang="scss" scoped>
.store {
  &__brand {
    padding-block: 20px;
    flex-shrink: 0;
    transition: 0.2s;
    margin-right: 30px;
    color: #2F2F2F;
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    line-height: 25.6px;

    @include hover {
      color: #FF6162;
    }
  }

  &__top {
    margin-top: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    & p {
      color: #2F2F2F;
      font-size: 20px;
      font-weight: 700;
      line-height: 22.4px;
    }
  }

  &__sorting {
    display: flex;
    align-items: center;
    gap: 15px;

    &-button {
      width: fit-content;
      font-size: 12px;
      padding: 10px 20px;
      display: flex;
      align-items: center;
      gap: 7px;
    }
  }

  &__grid {
    margin-top: 50px;
    display: grid;
    grid-template-columns: repeat(5, 220px);
    justify-content: space-between;
    row-gap: 65px;

    &-observer {
      width: 100%;
      height: 10px;
      background-color: transparent;
    }
  }

  &__empty {
    margin-block: 50px;
    color: #2F2F2F;
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    line-height: 22.4px;
  }
}

@media (max-width: 1300px) {
  .store {
    &__grid {
      grid-template-columns: repeat(4, 220px);
    }
  }
}

@media (max-width: 1000px) {
  .store {
    &__grid {
      grid-template-columns: repeat(3, 220px);
    }
  }
}

@media (max-width: 750px) {
  .store {
    &__top {
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;
    }

    &__grid {
      grid-template-columns: repeat(2, 220px);
    }
  }
}

@media (max-width: 500px) {
  .store {
    &__grid {
      grid-template-columns: repeat(1, 220px);
    }
  }
}
</style>